import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  LockReset as LockResetIcon,
} from "@mui/icons-material";
import api from "../config/axios";

function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [passwordData, setPasswordData] = useState({
    userId: null,
    username: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const fetchUsers = async () => {
    try {
      const response = await api.get("/api/users");
      setUsers(response.data);
    } catch (fetchError) {
      console.error("Erreur lors de la récupération des utilisateurs:", fetchError);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpen = () => {
    setFormData({ username: "", password: "" });
    setError("");
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setError("");
  };

  const handlePasswordOpen = (user) => {
    setPasswordData({
      userId: user.id,
      username: user.username,
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordError("");
    setPasswordOpen(true);
  };

  const handlePasswordClose = () => {
    setPasswordOpen(false);
    setPasswordError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    try {
      await api.post("/api/users", formData);
      await fetchUsers();
      handleClose();
    } catch (submitError) {
      const message =
        submitError.response?.data?.message ||
        "Erreur lors de la création de l'utilisateur";
      setError(message);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordError("");

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("Les mots de passe ne correspondent pas");
      return;
    }

    try {
      await api.put(`/api/users/${passwordData.userId}/password-reset`, {
        newPassword: passwordData.newPassword,
      });
      handlePasswordClose();
    } catch (submitError) {
      const message =
        submitError.response?.data?.message ||
        "Erreur lors de la réinitialisation du mot de passe";
      setPasswordError(message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) {
      try {
        await api.delete(`/api/users/${id}`);
        await fetchUsers();
      } catch (deleteError) {
        console.error("Erreur lors de la suppression:", deleteError);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4">Utilisateurs</Typography>
        <Button variant="contained" onClick={handleOpen}>
          Nouvel utilisateur
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom d'utilisateur</TableCell>
              <TableCell>Date de création</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>
                  {user.created_at
                    ? new Date(user.created_at).toLocaleString("fr-FR")
                    : "-"}
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleDelete(user.id)}>
                    <DeleteIcon />
                  </IconButton>
                  <IconButton onClick={() => handlePasswordOpen(user)}>
                    <LockResetIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Nouvel utilisateur</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nom d'utilisateur"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Mot de passe"
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              margin="normal"
              required
              helperText="12 caractères min. et 3/4 conditions (maj/min/chiffre/special)"
            />
            {error && (
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
            <DialogActions>
              <Button onClick={handleClose}>Annuler</Button>
              <Button type="submit" variant="contained">
                Créer
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={passwordOpen} onClose={handlePasswordClose}>
        <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handlePasswordSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Utilisateur"
              value={passwordData.username}
              margin="normal"
              disabled
            />
            <TextField
              fullWidth
              label="Nouveau mot de passe"
              type="password"
              value={passwordData.newPassword}
              onChange={(e) =>
                setPasswordData({
                  ...passwordData,
                  newPassword: e.target.value,
                })
              }
              margin="normal"
              required
              helperText="12 caractères min. et 3/4 conditions (maj/min/chiffre/special)"
            />
            <TextField
              fullWidth
              label="Confirmer le mot de passe"
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) =>
                setPasswordData({
                  ...passwordData,
                  confirmPassword: e.target.value,
                })
              }
              margin="normal"
              required
            />
            {passwordError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {passwordError}
              </Typography>
            )}
            <DialogActions>
              <Button onClick={handlePasswordClose}>Annuler</Button>
              <Button type="submit" variant="contained">
                Réinitialiser
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Users;
